import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating, Loading, EmptyState } from "@/components/shared";
import { Building2, ShieldCheck, MapPin, Star, MessageSquarePlus, Handshake } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DIMS = [
  ["honesty", "Honest about the role"],
  ["communication", "Kept me informed"],
  ["salary_transparency", "Transparent on salary"],
  ["cv_consent", "Asked before sending my CV"],
  ["would_use_again", "Would use again"],
];

function StarPick({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[#DDDAD1] p-3">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => onChange(i)} aria-label={`${label} ${i}`} className="transition-transform hover:scale-110">
            <Star className={`h-5 w-5 ${i <= value ? "fill-amber-500 text-amber-500" : "text-slate-600"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function RecruiterProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    honesty: 0, communication: 0, salary_transparency: 0, cv_consent: 0, would_use_again: 0,
    title: "", body: "", role_placed: "",
  });

  const load = () => {
    api.get(`/recruiters/${id}`).then(({ data }) => setRec(data)).catch(() => setRec(false));
    api.get(`/recruiters/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  };
  useEffect(() => { load(); window.scrollTo(0, 0); /* eslint-disable-next-line */ }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = DIMS.every(([k]) => form[k] > 0) && form.title.trim() && form.body.trim();

  const submit = async () => {
    if (!user) { toast.error("Please log in to review a recruiter."); nav("/login"); return; }
    setBusy(true);
    try {
      await api.post("/recruiter-reviews", { recruiter_id: id, ...form });
      toast.success("Thanks! Your review is live.");
      setShowForm(false);
      setForm({ honesty: 0, communication: 0, salary_transparency: 0, cv_consent: 0, would_use_again: 0, title: "", body: "", role_placed: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (rec === null) return <Loading />;
  if (rec === false) return <Loading label="Recruiter unavailable" />;

  return (
    <main className="mx-auto max-w-5xl px-5 py-10 lg:px-8">
      <div className="card-surface p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-teal-600">
              {rec.type === "agency" ? <Building2 className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              {rec.type === "agency" ? "Recruitment agency" : "Individual recruiter"}
            </p>
            <h1 className="mt-2 text-3xl font-bold text-ink">{rec.name}</h1>
            <p className="mt-3 max-w-xl text-slate-400">{rec.about}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(rec.markets || []).map((m) => (
                <span key={m} className="inline-flex items-center gap-1 rounded-full border border-[#DDDAD1] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-slate-400"><MapPin className="h-2.5 w-2.5 text-teal-400" />{m}</span>
              ))}
            </div>
          </div>
          <div className="text-center">
            <div className="font-heading text-5xl font-bold text-teal-400">{rec.avg_overall || "-"}</div>
            <StarRating value={rec.avg_overall} />
            <p className="mt-1 text-xs text-slate-500">{rec.review_count} reviews</p>
          </div>
        </div>

        {rec.review_count > 0 && (
          <div className="mt-6 grid gap-3 border-t border-[#DDDAD1] pt-6 sm:grid-cols-5">
            {DIMS.map(([k, label]) => (
              <div key={k} className="text-center">
                <div className="font-heading text-xl font-bold text-ink">{rec[`avg_${k}`] || "-"}</div>
                <p className="mt-1 text-[11px] leading-tight text-slate-500">{label}</p>
              </div>
            ))}
          </div>
        )}

        <button data-testid="review-recruiter-btn" onClick={() => setShowForm((s) => !s)} className="btn-teal mt-6 inline-flex items-center gap-2 text-sm">
          <MessageSquarePlus className="h-4 w-4" /> {showForm ? "Close" : "Review this recruiter"}
        </button>
      </div>

      {showForm && (
        <div data-testid="recruiter-review-form" className="card-surface mt-6 p-6 sm:p-8 animate-fade-up">
          <h2 className="text-xl font-bold text-ink">Your experience</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {DIMS.map(([k, label]) => (
              <StarPick key={k} label={label} value={form[k]} onChange={(v) => set(k, v)} testid={`dim-${k}`} />
            ))}
          </div>
          <div className="mt-4 space-y-4">
            <div><Label>Headline</Label><Input data-testid="rec-review-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Summarise your experience" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
            <div><Label>Your story</Label><Textarea data-testid="rec-review-body" value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="What was working with them like?" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
            <div><Label>Role they placed you in (optional)</Label><Input data-testid="rec-review-role" value={form.role_placed} onChange={(e) => set("role_placed", e.target.value)} className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
          </div>
          <button data-testid="rec-review-submit" onClick={submit} disabled={busy || !canSubmit} className="btn-teal mt-6 text-sm disabled:opacity-50">
            {busy ? "Posting…" : "Post review"}
          </button>
        </div>
      )}

      <section className="mt-10">
        <h2 className="text-xl font-bold text-ink">Reviews</h2>
        {reviews.length === 0 ? (
          <div className="mt-4"><EmptyState icon={Handshake} title="No reviews yet" subtitle="Be the first to share your experience with this recruiter." /></div>
        ) : (
          <div className="mt-4 space-y-4">
            {reviews.map((r) => (
              <div key={r.id} data-testid="recruiter-review" className="card-surface p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-ink">{r.title}</h3>
                    <p className="mt-1 text-xs text-slate-500">{r.role_placed ? `Placed as ${r.role_placed} · ` : ""}{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <StarRating value={r.rating_overall} />
                    <p className="mt-1 text-xs text-slate-500">{r.rating_overall?.toFixed(1)}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-300">{r.body}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {DIMS.map(([k, label]) => (
                    <span key={k} className="rounded-full border border-[#DDDAD1] px-2.5 py-1 text-[11px] text-slate-400">{label}: <span className="text-ink">{r[k]}</span></span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
